import type { IdNegocio, ParDeTokens } from '../api/operaciones';

/**
 * Sesión del panel: los tokens que devuelve la API (`TokenPair`) y el negocio fijado en ella. El cliente de la API lee
 * de aquí el token de acceso. Se guarda en sessionStorage para no perderla al recargar la pestaña y se borra al
 * cerrarla. El token de acceso dura 15 minutos: al caducar, el cliente de la API renueva la sesión con el de refresco
 * (`crearRenovador`, `refreshSession`), que rota en cada uso (la sesión dura 30 días renovándose con el uso, G208).
 *
 * El acceso de plataforma abierto (Soporte o Superadmin dentro de un negocio) no se guarda aquí: llega del servidor en
 * `platform_access` de getMyNavigation, también tras recargar (G137, ADR-0054).
 */

export interface Sesion {
    accessToken: string;
    refreshToken: string;
    /** Negocio fijado en la sesión; null hasta elegirlo (selectBusiness). */
    negocio: IdNegocio | null;
}

const CLAVE = 'reservaria.sesion';

export interface AlmacenSesion {
    actual(): Sesion | null;
    guardar(tokens: ParDeTokens): Sesion;
    borrar(): void;
    escuchar(oyente: (sesion: Sesion | null) => void): () => void;
}

export function sesionDe(tokens: ParDeTokens): Sesion {
    return { accessToken: tokens.access_token, refreshToken: tokens.refresh_token, negocio: tokens.business_id };
}

function esSesion(valor: unknown): valor is Sesion {
    if (typeof valor !== 'object' || valor === null) {
        return false;
    }
    const v = valor as Record<string, unknown>;
    return typeof v.accessToken === 'string' && typeof v.refreshToken === 'string' && (v.negocio === null || typeof v.negocio === 'string');
}

export function crearAlmacenSesion(almacen: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null): AlmacenSesion {
    let sesion: Sesion | null = null;
    try {
        const guardada: unknown = JSON.parse(almacen?.getItem(CLAVE) ?? 'null');
        sesion = esSesion(guardada) ? guardada : null;
    } catch {
        sesion = null;
    }
    const oyentes = new Set<(sesion: Sesion | null) => void>();

    function fijar(nueva: Sesion | null): void {
        sesion = nueva;
        try {
            if (nueva === null) {
                almacen?.removeItem(CLAVE);
            } else {
                almacen?.setItem(CLAVE, JSON.stringify(nueva));
            }
        } catch {
            // Sin almacenamiento (modo privado): la sesión vive solo en memoria.
        }
        oyentes.forEach((oyente) => {
            oyente(nueva);
        });
    }

    return {
        actual: () => sesion,
        guardar(tokens) {
            const nueva = sesionDe(tokens);
            fijar(nueva);
            return nueva;
        },
        borrar() {
            fijar(null);
        },
        escuchar(oyente) {
            oyentes.add(oyente);
            return () => {
                oyentes.delete(oyente);
            };
        },
    };
}

export function almacenDelNavegador(): Storage | null {
    try {
        return typeof window === 'undefined' ? null : window.sessionStorage;
    } catch {
        return null;
    }
}

/**
 * Renovación de la sesión para el cliente de la API (`OpcionesCliente.renovar`): pide tokens nuevos con el de refresco
 * (`refreshSession`) y los guarda. Una sola renovación a la vez: las peticiones que caducan juntas esperan la misma, y una
 * que falló con un token que ya se ha renovado solo se repite. Si no se puede renovar (refresco caducado, revocado o
 * reutilizado), la sesión se acaba: el 401 llega al panel, que vuelve a pedir que se entre.
 */
export function crearRenovador(almacen: AlmacenSesion, refrescar: (tokenRefresco: string) => Promise<ParDeTokens>): (tokenCaducado: string) => Promise<boolean> {
    let enCurso: Promise<boolean> | null = null;
    return (tokenCaducado) => {
        const actual = almacen.actual();
        if (actual === null) {
            return Promise.resolve(false);
        }
        if (actual.accessToken !== tokenCaducado) {
            return Promise.resolve(true);
        }
        if (enCurso !== null) {
            return enCurso;
        }
        const conEste = actual.refreshToken;
        enCurso = refrescar(conEste)
            .then((tokens) => {
                // Si mientras tanto se cerró la sesión o se entró con otra, no se pisa.
                if (almacen.actual()?.refreshToken !== conEste) {
                    return false;
                }
                almacen.guardar(tokens);
                return true;
            })
            .catch(() => false)
            .finally(() => {
                enCurso = null;
            });
        return enCurso;
    };
}
